"use client";

import styles from "./agency-application-modal.module.css";
import { MdClose } from "react-icons/md";
import {
  FiUser,
  FiBriefcase,
  FiMapPin,
  FiCalendar,
  FiHome,
  FiMessageSquare,
} from "react-icons/fi";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { PendingAgencyApplication } from "../../types";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { env } from "@/lib/env";
import { toast } from "react-toastify";
import { ApplicationModalSkeleton } from "../../../admin-agencies-applications/modals/application-modal";
import { ApiError } from "@/types/types";
import { useTranslations } from "next-intl";
import { apiFetch } from "@/lib/apiFetch";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

type Props = {
  applicationId: string;
  dialogRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  onCloseAction: () => void;
};

const PROFILESENDPOINTURL = env.NEXT_PROFILES_API_URL;

export default function AgencyApplicationModal({
  applicationId,
  dialogRef,
  isOpen,
  onCloseAction,
}: Props) {
  const [message, setMessage] = useState("");
  const [application, setApplication] =
    useState<PendingAgencyApplication | null>(null);
  const [loading, setLoading] = useState(false);
  const { userData } = useUserAuthDataContext();
  const [sending, setSending] = useState(false);
  const t = useTranslations("AgencyApplicationModal");

  useEffect(() => {
    if (!isOpen) {
      setMessage("");
      setSending(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !applicationId) return;

    let cancelled = false;

    const fetchApplication = async () => {
      if (userData.sessionId === null) {
        toast("Please reauthenticate before performing this action.", {
          type: "error",
        });
        return;
      }
      setLoading(true);

      try {
        const res = await apiFetch(
          `/api/agency/getAgencyApplication?request_id=${applicationId}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (!res.ok) {
          const apiErr = (await res.json()) as ApiError;
          throw new Error(apiErr.message);
        }

        const data = await res.json();

        if (!cancelled) {
          setApplication(data);
        }
      } catch (err: any) {
        if (cancelled) return;

        const errorMessage =
          err instanceof Error ? err.message : "Failed to load application.";

        toast(errorMessage, { type: "error" });
        onCloseAction();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchApplication();

    return () => {
      cancelled = true;
    };
  }, [isOpen, applicationId]);

  useDialogRouting("AgencyApplicationModal", isOpen, onCloseAction);

  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      closeBtnRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (loading) {
    return <ApplicationModalSkeleton dialogRef={dialogRef} isAdmin={false} />;
  }

  if (!application) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        e.stopPropagation();
        onCloseAction();
      }}
    >
      <div
        ref={dialogRef}
        className={`${styles.modal} ${styles.show}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agency-application-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* === Header === */}
        <header className={styles.header}>
          <div className={styles.headerTexts}>
            <h2 id="agency-application-title" className={styles.title}>
              {application.agency_name}
            </h2>
            <p className={styles.subtitle}>
              {t("submittedOn")}{" "}
              {new Date(application.created_at).toLocaleDateString()}
            </p>
          </div>

          <button
            ref={closeBtnRef}
            className={styles.closeBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCloseAction();
            }}
            aria-label={t("closeModal")}
          >
            <MdClose />
          </button>
        </header>

        <div className={styles.divider} />

        {/* === Status === */}
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>{t("applicationStatus")}</span>
          <span
            className={`${styles.statusBadge} ${styles[application.status]}`}
            aria-live="polite"
          >
            {t(application.status)}
          </span>
        </div>

        {/* === Overview === */}
        <section className={styles.overview}>
          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiUser />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("applicant")}</span>
              <span className={styles.v}>{application.full_name}</span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiBriefcase />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("role")}</span>
              <span className={styles.v}>{t(`roles.${application.role}`)}</span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiCalendar />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("founded")}</span>
              <span className={styles.v}>{application.founded_year}</span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiHome />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("avgListings")}</span>
              <span className={styles.v}>{application.avg_listings}</span>
            </div>
          </div>

          <div className={styles.kv}>
            <span className={styles.kvIcon}>
              <FiHome />
            </span>
            <div className={styles.kvText}>
              <span className={styles.k}>{t("priceRange")}</span>
              <span className={styles.v}>
                {application.price_range != ""
                  ? t(`priceRanges.${application.price_range}`)
                  : "-"}
              </span>
            </div>
          </div>
        </section>

        {/* === Details Grid === */}
        <section className={styles.grid}>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>{t("specializations")}</h3>
            <ul className={styles.list}>
              {Object.entries(application.specializations)
                .filter(([, v]) => v)
                ?.map(([k]) => (
                  <li key={k}>{t(k)}</li>
                ))}
            </ul>
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>{t("acquisition")}</h3>
            <ul className={styles.list}>
              {Object.entries(application.acquisition)
                .filter(([, v]) => v)
                ?.map(([k]) => (
                  <li key={k}>{t(k)}</li>
                ))}
            </ul>
          </div>

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>{t("locations")}</h3>
            <p className={styles.cardText}>{application.locations}</p>
          </div>

          <div className={`${styles.card} ${styles.cardWide}`}>
            <h3 className={styles.cardTitle}>{t("motivation")}</h3>
            <p className={styles.cardText}>{application.motivation}</p>
          </div>
        </section>

        {/* === Comments === */}
        <section className={styles.comments}>
          <h3 className={styles.commentsTitle}>
            <FiMessageSquare /> {t("messages")}
          </h3>

          {application.notes?.length === 0 ? (
            <p className={styles.noComments}>{t("noMessage")}</p>
          ) : (
            <ul className={styles.commentsList}>
              {application.notes?.map((c, idx) => (
                <li key={idx} className={styles.comment}>
                  <img
                    src={c.author_photo_url}
                    alt={t("profilePhotoAlt", { name: c.author_name })}
                    className={styles.avatar}
                  />
                  <div>
                    <div className={styles.commentHeader}>
                      <strong>{c.author_name}</strong>
                      <span>{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className={styles.commentText}>{c.message}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        {/* === Reply box (only if pending) === */}
        {application.status === "pending" && (
          <div className={styles.replyBox}>
            <img
              src={userData.user_photo}
              alt={t("profilePhotoAlt", { name: userData.complete_name })}
              className={styles.replyAvatar}
            />

            <div className={styles.replyContent}>
              <label htmlFor="user-reply" className={styles.srOnly}>
                {t("userReplyLabel")}
              </label>
              <textarea
                id="user-reply"
                className={styles.replyInput}
                placeholder={t("writeReplyUser")}
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div className={styles.replyActions}>
                <span className={styles.replyHint}>{t("messageVisible")}</span>

                <button
                  className={styles.replyButton}
                  disabled={sending || !message.trim()}
                  aria-disabled={sending || !message.trim()}
                  onClick={async () => {
                    if (!message.trim() || !userData.sub) return;

                    if (!userData.sessionId) {
                      toast(
                        "Please reauthenticate before performing this action.",
                        {
                          type: "error",
                        },
                      );
                      return;
                    }

                    setSending(true);

                    try {
                      const res = await fetch(
                        `${PROFILESENDPOINTURL}/sendApplicationMessage`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: userData.sessionId,
                          },
                          body: JSON.stringify({
                            request_id: applicationId,
                            message,
                          }),
                        },
                      );

                      if (!res.ok) {
                        const err = await res.json();
                        throw new Error(
                          err.message ?? "Failed to send message",
                        );
                      }

                      const newNote = {
                        author_sub: userData.sub,
                        author_name: userData.complete_name,
                        author_photo_url: userData.user_photo,
                        message,
                        created_at: new Date().toISOString(),
                      };

                      // optimistic UI update
                      setApplication((prev) =>
                        prev
                          ? {
                              ...prev,
                              notes: [...prev.notes, newNote],
                            }
                          : prev,
                      );

                      setMessage("");
                    } catch (err: any) {
                      toast(
                        err?.message === "Failed to fetch"
                          ? "Unable to send message."
                          : err.message,
                        { type: "error" },
                      );
                    } finally {
                      setSending(false);
                    }
                  }}
                >
                  {t("sendReply")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
